import { ROUTES } from './routes';
import { materialGlyph, type GlyphRef } from '../theme/glyph';

/**
 * Navigation destinations — single source of truth for the app's top-level
 * "go to a page" targets.
 *
 * The Cmd-K palette's "Go to" group is DERIVED from this list, so adding a new
 * page here (in ONE place) makes it reachable from the palette with the right
 * label, synonyms, and visibility — no separate palette list to keep in sync.
 *
 * Self-maintaining guarantee: the `navDestinations` drift-guard test fails CI if
 * any static `ROUTES` entry is neither a destination here nor listed in
 * {@link NAV_EXCLUDED_ROUTES} with a reason. So a newly-added route CANNOT
 * silently miss the palette — someone has to consciously classify it.
 *
 * (The sidebar still defines its own nav inline; unifying it onto this SSOT is a
 * follow-up. This SSOT + the drift guard already make the palette complete.)
 */

export interface NavVisibilityContext {
  /** Read-only role (GUEST/VIEWER) — hides Trash / Audit, matching the sidebar. */
  readonly isReadOnly: boolean;
  /** Knowledge-graph entitlement (`useCanUseFeature('knowledgeGraph')`). */
  readonly graphEnabled: boolean;
}

export interface NavDestination {
  readonly id: string;
  readonly label: string;
  readonly keywords: readonly string[];
  readonly glyph: GlyphRef;
  /** A static `ROUTES` value (never a param-route builder). */
  readonly route: string;
  readonly visible: (ctx: NavVisibilityContext) => boolean;
}

const ALWAYS = (): boolean => true;

export const NAV_DESTINATIONS: ReadonlyArray<NavDestination> = [
  {
    id: 'goto-dashboard',
    label: 'Dashboard',
    keywords: ['home', 'start'],
    glyph: materialGlyph('home'),
    route: ROUTES.dashboard,
    visible: ALWAYS,
  },
  {
    id: 'goto-collections',
    label: 'Collections',
    keywords: ['projects', 'folders'],
    glyph: materialGlyph('folder'),
    route: ROUTES.collections,
    visible: ALWAYS,
  },
  {
    id: 'goto-artifacts',
    label: 'Artifacts',
    keywords: ['notes', 'files', 'all artifacts'],
    glyph: materialGlyph('description'),
    route: ROUTES.artifacts,
    visible: ALWAYS,
  },
  {
    id: 'goto-protocols',
    label: 'Protocols',
    keywords: ['methods', 'procedures'],
    glyph: materialGlyph('science'),
    route: ROUTES.protocols,
    visible: ALWAYS,
  },
  {
    id: 'goto-equipment',
    label: 'Equipment',
    keywords: ['instruments', 'devices', 'register'],
    glyph: materialGlyph('precision_manufacturing'),
    route: ROUTES.equipment,
    visible: ALWAYS,
  },
  {
    id: 'goto-graph',
    label: 'Workspace visualizer',
    keywords: ['knowledge graph', 'graph', 'visualiser', 'map', 'relationships', 'connections'],
    glyph: materialGlyph('neurology'),
    route: ROUTES.graph,
    visible: (ctx) => ctx.graphEnabled,
  },
  {
    id: 'goto-trash',
    label: 'Trash',
    keywords: ['deleted', 'bin', 'restore'],
    glyph: materialGlyph('delete_outline'),
    route: ROUTES.trash,
    visible: (ctx) => !ctx.isReadOnly,
  },
  {
    id: 'goto-audit',
    label: 'Audit logs',
    keywords: ['history', 'activity', 'logs'],
    glyph: materialGlyph('history'),
    route: ROUTES.audit,
    visible: (ctx) => !ctx.isReadOnly,
  },
];

/**
 * Static `ROUTES` that are deliberately NOT Cmd-K "Go to" destinations, each with
 * a reason. Keyed by the `ROUTES` key. The drift-guard test requires every static
 * route to be here OR in {@link NAV_DESTINATIONS}, so adding a route forces a
 * conscious "is this navigable from the palette?" decision.
 */
export const NAV_EXCLUDED_ROUTES: Readonly<Record<string, string>> = {
  root: 'index redirect to /dashboard',
  onboarding: 'first-run flow, not a launcher destination',
  connectorCallback: 'OAuth return landing (rendered outside ProtectedRoute)',
  account: 'legacy alias — renders the same page as /settings',
  admin: 'internal staff page, deliberately hidden from non-admins',
  liveSession: 'entry into a live capture session — an action/mode, not a browse destination',
  liveCall:
    'internal LiveKit call preview behind live-call-preview-enabled: a mode, not a browse destination',
  sharedWithMe: 'route-less dead nav constant (issue #289)',
  settings: 'covered by the per-tab settings command entries (SETTINGS_TABS)',
  auth: 'auth flow',
  resetPassword: 'auth flow',
  authAction: 'auth flow',
  verifyEmail: 'auth flow',
  verifyEmailPrompt: 'auth flow',
  recoverEmail: 'auth flow',
  authWorkos: 'auth flow',
  login: 'legacy auth redirect',
  mobileCapture: 'mobile-only route',
  mobileBird: 'mobile-only route',
  mobileWorkspace: 'mobile-only route',
  mobileLive: 'mobile-only route',
  mobileLiveSession: 'mobile-only route',
  mobileNotes: 'mobile-only route',
  projects: 'legacy redirect → /collections',
  captureStream: 'legacy redirect → /protocols (runs live inside Protocols)',
};
